export interface KnowledgeBasePollingVisibilityControllerOptions {
  isVisible?: () => boolean
  subscribeToVisibilityChange?: (listener: () => void) => () => void
}

function defaultIsVisible(): boolean {
  if (typeof document === "undefined") return true
  return document.visibilityState === "visible"
}

function defaultSubscribeToVisibilityChange(listener: () => void): () => void {
  if (typeof document === "undefined") return () => {}

  document.addEventListener("visibilitychange", listener)
  return () => {
    document.removeEventListener("visibilitychange", listener)
  }
}

export class KnowledgeBasePollingVisibilityController {
  private readonly isVisibleFn: () => boolean
  private readonly listeners = new Set<(isVisible: boolean) => void>()
  private readonly unsubscribeVisibilityChange: () => void
  private lastVisibility: boolean

  constructor(options: KnowledgeBasePollingVisibilityControllerOptions = {}) {
    this.isVisibleFn = options.isVisible || defaultIsVisible
    this.lastVisibility = this.isVisibleFn()
    this.unsubscribeVisibilityChange =
      options.subscribeToVisibilityChange?.(this.handleVisibilityChange) ||
      defaultSubscribeToVisibilityChange(this.handleVisibilityChange)
  }

  canPoll(): boolean {
    return this.isVisibleFn()
  }

  subscribe(listener: (isVisible: boolean) => void): () => void {
    this.listeners.add(listener)

    return () => {
      this.listeners.delete(listener)
    }
  }

  dispose(): void {
    this.unsubscribeVisibilityChange()
    this.listeners.clear()
  }

  private readonly handleVisibilityChange = () => {
    const nextVisibility = this.isVisibleFn()
    if (nextVisibility === this.lastVisibility) return

    this.lastVisibility = nextVisibility
    for (const listener of this.listeners) {
      listener(nextVisibility)
    }
  }
}
